import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {css} from "@emotion/css";
import SortableTable, {TableData} from "@common/SortableTable";
import {sizes} from "patient-ping-remedy/packages/theme";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useAlertStore} from "@/store/alert_store";
import {AxiosError} from "axios";
import {ToastType} from "patient-ping-remedy/packages/toast";
import Helpers from "@/helpers/helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import Header from "@common/pages/Header";
import CreateFormFieldNameModal from "@/components/pages/settings/form_fields/CreateFormFieldNameModal";
import ConfirmDeleteModal from "@common/ConfirmDeleteModal";

export default function FormFieldNameTable() {
  const {carecoApi} = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const {addAlert} = useAlertStore();

  const [tableData, setTableData] = useState<TableData[]>([]);
  const [removeFormName, setRemoveFormName] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);


  const {data: formFieldNames, isLoading, error, isFetching} = useQuery({
    queryKey: ["formFieldNames"],
    queryFn: () => carecoApi!.getFormFieldNames(),
    enabled: !!carecoApi,
  });

  const {mutate: removeFormFieldName, isPending: isPendingRemoveFormFieldName} = useMutation({
    mutationFn: (formName: string) => carecoApi!.deleteFormFieldName(formName),
    onSuccess: () => {
      addAlert({content: "Form field name deleted successfully.", type: ToastType.SUCCESS});
      void queryClient.invalidateQueries({queryKey: ["formFieldNames"]});
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to delete form field name. ${Helpers.traceId(error)}`, error);
    },
  });

  const formFieldNamesList = useMemo(
    () =>
      tableData
        .filter((data) => typeof data.name.value === "string")
        .map((data) => ({
          name: {value: String(data.name.value)},
        })),
    [tableData]
  );

  const mapFormFieldNames = useCallback(
    (formFieldName: string): TableData => ({
      name: {
        tableHeader: "Name",
        key: "name",
        type: "string",
        value: formFieldName,
        displayValue: formFieldName,
        columnWidth: "20%",
      },
      button: {
        tableHeader: "Remove",
        key: "remove",
        type: "custom",
        value: formFieldName,
        displayValue: (
          <Button
            testId={`remove-form-field-name-${formFieldName}`}
            styleType={StyleType.TERTIARY}
            onClick={() => {
              setRemoveFormName(formFieldName);
              setIsRemoveModalOpen(true);
            }}
            disabled={isPendingRemoveFormFieldName}
          >
            Remove
          </Button>
        ),
        columnWidth: "5%",
      },
    }),
    [isPendingRemoveFormFieldName]
  );

  useEffect(() => {
    if (formFieldNames) {
      setTableData(formFieldNames.map(mapFormFieldNames));
    }
  }, [formFieldNames, mapFormFieldNames]);

  const handleConfirmRemove = useCallback(() => {
    removeFormFieldName(removeFormName);
    setIsRemoveModalOpen(false);
  }, [removeFormFieldName, removeFormName]);

  return (
    <>
      <Header subtitle={true}>Form field names</Header>
      <SortableTable
        id={"form-field-names-table"}
        data={tableData}
        loading={isLoading || isFetching}
        loadError={error}
        header={["Name",""]}
        sortableColumns={[]}
        searchColumns={["name"]}
        searchPlaceholder={"Search by name"}
        excludeMixpanel={true}
      >
        <Button
          testId={"add-form-field-name-button"}
          onClick={() => setIsModalOpen(true)}
          disabled={isLoading}
        >
          <Icon iconClass={ICONS.plus} className={css({ marginRight: sizes.xSmall })} />
          Add
        </Button>
      </SortableTable>
      {isModalOpen && (
        <CreateFormFieldNameModal
          close={() => setIsModalOpen(false)}
          tableData={formFieldNamesList}
        />
      )}
      {isRemoveModalOpen && (
        <ConfirmDeleteModal
          close={() => setIsRemoveModalOpen(false)}
          setConfirmRemoveModalOpen={handleConfirmRemove}
          name={removeFormName}
          tableName="form field names"
        />
      )}
    </>
  );
}
